import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Building, Loader2, Search, DollarSign, Users, FileText, Edit, Lock, RefreshCw, Trash2, TrendingUp, CheckCircle2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
// Removed Collapsible import - using simple state-based expansion
import { Check, ChevronsUpDown, ChevronDown, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import DateRangePicker from '@/components/portal/DateRangePicker';
import EditCommissionPolicyDialog from '@/components/portal/EditCommissionPolicyDialog';
import AddCommissionModal from '@/components/portal/AddCommissionModal';
import EditCommissionModal from '@/components/portal/EditCommissionModal';
import DeleteCommissionDialog from '@/components/portal/DeleteCommissionDialog';
import AgentListPanel from '@/components/portal/AgentListPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CommissionTracker({ userInfo }) {
  const [carriers, setCarriers] = useState([]);
  const [loadingCarriers, setLoadingCarriers] = useState(true);
  const [selectedAgencies, setSelectedAgencies] = useState([]);
  const [selectedCarrier, setSelectedCarrier] = useState('');
  const [policies, setPolicies] = useState([]);
  const [loadingPolicies, setLoadingPolicies] = useState(false);
  const [expandedPolicies, setExpandedPolicies] = useState({});
  const [selectedPolicyId, setSelectedPolicyId] = useState(null);
  const [editedPolicyId, setEditedPolicyId] = useState(null);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showAddCommissionModal, setShowAddCommissionModal] = useState(false);
  const [editingCommission, setEditingCommission] = useState(null);
  const [showEditCommissionModal, setShowEditCommissionModal] = useState(false);
  const [deletingCommission, setDeletingCommission] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [expandedNotes, setExpandedNotes] = useState({});
  
  // Slide-out panel state
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [showCommissionsPanel, setShowCommissionsPanel] = useState(false);
  
  // Policy delete modal
  const [showDeletePolicyModal, setShowDeletePolicyModal] = useState(false);
  const [deletingPolicy, setDeletingPolicy] = useState(null);
  const [deletingPolicyReason, setDeletingPolicyReason] = useState('');
  const [deletingPolicy_loading, setDeletingPolicy_loading] = useState(false);
  
  // For searchable dropdowns
  const [agencyOpen, setAgencyOpen] = useState(false);
  const [carrierOpen, setCarrierOpen] = useState(false);
  
  // Filters for policies
  const [policySearchQuery, setPolicySearchQuery] = useState('');
  const [policySearchExpanded, setPolicySearchExpanded] = useState(false);
  const [policyDateRange, setPolicyDateRange] = useState(null);
  const [dateFilterType, setDateFilterType] = useState('effective'); // 'effective' or 'created'
  const [commissionFilter, setCommissionFilter] = useState('all'); // all, with, without
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState([]);
  
  // Filters for commissions
  const [commissionSearchQuery, setCommissionSearchQuery] = useState('');
  const [selectedPaidSplitStatuses, setSelectedPaidSplitStatuses] = useState([]);
  const [paidSplitOpen, setPaidSplitOpen] = useState(false);
  
  // Pagination
  const [policyPage, setPolicyPage] = useState(1);
  const [commissionPage, setCommissionPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  
  const fflAgencyAccess = userInfo?.agent_ffl_agency_access || [];

  useEffect(() => {
    fetchCarriers();
    // Set all agencies as default and fetch initial policies
    if (fflAgencyAccess.length > 0) {
      const allAgencyIds = fflAgencyAccess.map(agency => agency.id);
      setSelectedAgencies(allAgencyIds);
      fetchPolicies(allAgencyIds, null);
    }
  }, []);

  // Scroll to edited policy after policies are loaded and rendered
  useEffect(() => {
    if (editedPolicyId && !loadingPolicies && policies.length > 0) {
      // Wait for DOM to update
      setTimeout(() => {
        // Expand the edited policy
        setExpandedPolicies(prev => ({ ...prev, [editedPolicyId]: true }));
        
        // Scroll to the policy
        const element = document.getElementById(`policy-${editedPolicyId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        
        // Clear the edited policy ID
        setEditedPolicyId(null);
      }, 300);
    }
  }, [editedPolicyId, loadingPolicies, policies]);

  const fetchCarriers = async () => {
    try {
      setLoadingCarriers(true);
      const response = await axios.get(`${API}/meta/pms/carrier`);
      setCarriers(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error fetching carriers:', err);
      toast.error('Failed to load carriers');
    } finally {
      setLoadingCarriers(false);
    }
  };

  const fetchPolicies = async (agencyIds, carrierId) => {
    try {
      setLoadingPolicies(true);
      
      // Use POST with JSON body - send array of agency IDs
      const response = await axios.post('https://api1.simplyworkcrm.com/api:JBYRWqH3/portal/internal/commission/policies', {
        agency_id: Array.isArray(agencyIds) ? agencyIds : [agencyIds],
        carrier_id: carrierId
      });
      
      // Handle response - could be array directly or wrapped in {policies: [...]}
      let policiesData = [];
      if (Array.isArray(response.data)) {
        policiesData = response.data;
      } else if (response.data && Array.isArray(response.data.policies)) {
        policiesData = response.data.policies;
      }
      
      // Group policies by policy ID and merge commissions
      const groupedPolicies = {};
      policiesData.forEach(policyData => {
        const policyId = policyData.id;
        
        if (!groupedPolicies[policyId]) {
          // Initialize policy with empty commissions and splits arrays
          groupedPolicies[policyId] = {
            ...policyData,
            commission_of_policy: [],
            split_of_policy: []
          };
        }
        
        // Add commissions from this record
        if (Array.isArray(policyData.commission_of_policy)) {
          groupedPolicies[policyId].commission_of_policy.push(...policyData.commission_of_policy);
        } else if (policyData.commission_of_policy && typeof policyData.commission_of_policy === 'object') {
          groupedPolicies[policyId].commission_of_policy.push(policyData.commission_of_policy);
        }
        
        // Add splits from this record
        if (Array.isArray(policyData.split_of_policy)) {
          groupedPolicies[policyId].split_of_policy.push(...policyData.split_of_policy);
        } else if (policyData.split_of_policy && typeof policyData.split_of_policy === 'object') {
          groupedPolicies[policyId].split_of_policy.push(policyData.split_of_policy);
        }
      });
      
      // Normalize the grouped policies
      const normalizedPolicies = Object.values(groupedPolicies).map(policyData => {
        // Format created_at timestamp to date
        let formattedCreatedDate = 'N/A';
        if (policyData.created_at) {
          try {
            const timestamp = policyData.created_at < 10000000000 ? policyData.created_at * 1000 : policyData.created_at;
            formattedCreatedDate = format(new Date(timestamp), 'MMM dd, yyyy');
          } catch (err) {
            formattedCreatedDate = 'N/A';
          }
        }
        
        // Format effective date (initial_draft_date) to match created date format
        let formattedEffectiveDate = 'N/A';
        if (policyData.initial_draft_date) {
          try {
            // Parse YYYY-MM-DD format and format it consistently
            const [year, month, day] = policyData.initial_draft_date.split('-').map(Number);
            formattedEffectiveDate = format(new Date(year, month - 1, day), 'MMM dd, yyyy');
          } catch (err) {
            formattedEffectiveDate = 'N/A';
          }
        }
        
        return {
          ...policyData,
          created_date: formattedCreatedDate,
          effective_date: formattedEffectiveDate,
          initial_draft_date_raw: policyData.initial_draft_date, // Keep raw value for filtering
          created_timestamp: policyData.created_at,
          status_name: policyData.policyStatus || policyData.status_name || 'Unknown',
          isLocked: policyData.isLocked === true || policyData.isLocked === 1
        };
      });
      
      setPolicies(normalizedPolicies);
    } catch (err) {
      console.error('Error fetching policies:', err);
      toast.error('Failed to load commission policies');
      setPolicies([]);
    } finally {
      setLoadingPolicies(false);
    }
  };

  const handleSearch = () => {
    if (selectedAgencies.length === 0) {
      toast.error('Please select at least one agency');
      return;
    }
    
    fetchPolicies(selectedAgencies, selectedCarrier || null);
  };

  const handleReset = () => {
    if (fflAgencyAccess.length > 0) {
      const allAgencyIds = fflAgencyAccess.map(agency => agency.id);
      setSelectedAgencies(allAgencyIds);
      setSelectedCarrier('');
      setPolicySearchQuery('');
      setPolicyDateRange(null);
      setCommissionFilter('all');
      setSelectedStatuses([]);
      setCommissionSearchQuery('');
      setSelectedPolicyId(null);
      fetchPolicies(allAgencyIds, null);
    }
  };

  const handleCommissionClick = (commission) => {
    // Filter policy table to show only the policy this commission belongs to
    setSelectedPolicyId(commission.policy_id);
  };

  const togglePolicy = (policyId) => {
    setExpandedPolicies(prev => ({
      ...prev,
      [policyId]: !prev[policyId]
    }));
  };

  const handlePolicyClick = (policy) => {
    setSelectedPolicy(policy);
    setShowCommissionsPanel(true);
    setSelectedPolicyId(policy.id);
  };

  const handleCloseCommissionsPanel = () => {
    setShowCommissionsPanel(false);
    setSelectedPolicy(null);
  };

  const handleEditPolicy = (policy) => {
    setEditingPolicy(policy);
    setShowEditDialog(true);
  };

  const handleSavePolicy = () => {
    // Store the edited policy ID to keep it expanded after refresh
    if (editingPolicy) {
      setEditedPolicyId(editingPolicy.id);
    }
    // Refresh policies to get clean data from API after edit
    if (selectedAgencies.length > 0) {
      fetchPolicies(selectedAgencies, selectedCarrier || null);
    }
    toast.success('Policy updated successfully');
  };

  const handleAddCommissionSuccess = () => {
    // Refresh policies to get updated commission data
    if (selectedAgencies.length > 0) {
      fetchPolicies(selectedAgencies, selectedCarrier || null);
    }
  };

  const handleEditCommission = (commission) => {
    setEditingCommission(commission);
    setShowEditCommissionModal(true);
  };

  const handleDeleteCommission = (commission) => {
    setDeletingCommission(commission);
    setShowDeleteDialog(true);
  };

  const handleDeletePolicy = (policy) => {
    setDeletingPolicy(policy);
    setDeletingPolicyReason('');
    setShowDeletePolicyModal(true);
  };

  const handleConfirmDeletePolicy = async () => {
    // Validate that reason is provided
    if (!deletingPolicyReason.trim()) {
      toast.error('Please provide a reason for deleting this policy');
      return;
    }

    try {
      setDeletingPolicy_loading(true);
      
      const deletePayload = {
        policy_id: deletingPolicy.id,
        reason: deletingPolicyReason.trim(),
        user_id: userInfo.user_id
      };
      
      await axios.post(
        `${API}/portal/myPolicies/delete`,
        deletePayload
      );
      
      toast.success('Policy deleted successfully');
      setShowDeletePolicyModal(false);
      setDeletingPolicy(null);
      setDeletingPolicyReason('');
      
      // Refresh policies
      if (selectedAgencies.length > 0) {
        fetchPolicies(selectedAgencies, selectedCarrier || null);
      }
    } catch (err) {
      console.error('Error deleting policy:', err);
      toast.error(err.response?.data?.message || 'Failed to delete policy');
    } finally {
      setDeletingPolicy_loading(false);
    }
  };

  const handleCommissionUpdateSuccess = () => {
    // Refresh policies
    if (selectedAgencies.length > 0) {
      fetchPolicies(selectedAgencies, selectedCarrier || null);
    }
  };

  const getSelectedPolicy = () => {
    return policies.find(p => p.id === selectedPolicyId);
  };

  // Get unique policy statuses for filter
  // Get unique statuses for filter (based on policies filtered by everything EXCEPT status)
  const uniqueStatuses = useMemo(() => {
    let filtered = [...policies];
    
    // Apply search filter
    if (policySearchQuery.trim()) {
      const searchTerms = policySearchQuery.toLowerCase().split(',').map(term => term.trim()).filter(Boolean);
      filtered = filtered.filter(p => {
        const searchableText = `${p.policy_number || ''} ${p.client || ''} ${p.agentOwner_name || ''} ${p.agentOwner_npn || ''}`.toLowerCase();
        return searchTerms.some(term => searchableText.includes(term));
      });
    }
    
    // Apply date filter
    if (policyDateRange && policyDateRange.from && policyDateRange.to) {
      filtered = filtered.filter(p => {
        try {
          let policyDate;
          if (dateFilterType === 'effective') {
            if (!p.initial_draft_date_raw && !p.initial_draft_date) return false;
            const dateStr = p.initial_draft_date_raw || p.initial_draft_date;
            if (!dateStr || typeof dateStr !== 'string') return false;
            
            const parts = dateStr.split('-');
            if (parts.length !== 3) return false;
            
            const [year, month, day] = parts.map(Number);
            if (isNaN(year) || isNaN(month) || isNaN(day)) return false;
            
            policyDate = new Date(year, month - 1, day);
          } else {
            if (!p.created_timestamp) return false;
            const timestamp = p.created_timestamp < 10000000000 ? p.created_timestamp * 1000 : p.created_timestamp;
            policyDate = new Date(timestamp);
          }
          
          if (isNaN(policyDate.getTime())) return false;
          
          const normalizedPolicyDate = new Date(policyDate);
          normalizedPolicyDate.setHours(0, 0, 0, 0);
          const startDate = new Date(policyDateRange.from);
          startDate.setHours(0, 0, 0, 0);
          const endDate = new Date(policyDateRange.to);
          endDate.setHours(23, 59, 59, 999);
          return normalizedPolicyDate >= startDate && normalizedPolicyDate <= endDate;
        } catch (err) {
          console.warn('Error filtering by date:', p.id, err);
          return false;
        }
      });
    }
    
    // Apply commission filter
    if (commissionFilter === 'with') {
      filtered = filtered.filter(p => p.commission_of_policy && p.commission_of_policy.length > 0);
    } else if (commissionFilter === 'without') {
      filtered = filtered.filter(p => !p.commission_of_policy || p.commission_of_policy.length === 0);
    }
    
    // Apply agent filter
    if (selectedAgents.length > 0) {
      filtered = filtered.filter(p => {
        const agentKey = `${p.agentOwner_name}|${p.agentOwner_npn || ''}`;
        return selectedAgents.includes(agentKey);
      });
    }
    
    const statuses = new Set(filtered.map(p => p.status_name).filter(Boolean));
    return Array.from(statuses).sort();
  }, [policies, policySearchQuery, policyDateRange, dateFilterType, commissionFilter, selectedAgents]);

  // Get unique agents for filter (based on policies filtered by everything EXCEPT agent)
  const uniqueAgents = useMemo(() => {
    let filtered = [...policies];
    
    // Apply search filter
    if (policySearchQuery.trim()) {
      const searchTerms = policySearchQuery.toLowerCase().split(',').map(term => term.trim()).filter(Boolean);
      filtered = filtered.filter(p => {
        const searchableText = `${p.policy_number || ''} ${p.client || ''} ${p.agentOwner_name || ''} ${p.agentOwner_npn || ''}`.toLowerCase();
        return searchTerms.some(term => searchableText.includes(term));
      });
    }
    
    // Apply date filter
    if (policyDateRange && policyDateRange.from && policyDateRange.to) {
      filtered = filtered.filter(p => {
        try {
          let policyDate;
          if (dateFilterType === 'effective') {
            if (!p.initial_draft_date_raw && !p.initial_draft_date) return false;
            const dateStr = p.initial_draft_date_raw || p.initial_draft_date;
            if (!dateStr || typeof dateStr !== 'string') return false;
            
            const parts = dateStr.split('-');
            if (parts.length !== 3) return false;
            
            const [year, month, day] = parts.map(Number);
            if (isNaN(year) || isNaN(month) || isNaN(day)) return false;
            
            policyDate = new Date(year, month - 1, day);
          } else {
            if (!p.created_timestamp) return false;
            const timestamp = p.created_timestamp < 10000000000 ? p.created_timestamp * 1000 : p.created_timestamp;
            policyDate = new Date(timestamp);
          }
          
          if (isNaN(policyDate.getTime())) return false;
          
          const normalizedPolicyDate = new Date(policyDate);
          normalizedPolicyDate.setHours(0, 0, 0, 0);
          const startDate = new Date(policyDateRange.from);
          startDate.setHours(0, 0, 0, 0);
          const endDate = new Date(policyDateRange.to);
          endDate.setHours(23, 59, 59, 999);
          return normalizedPolicyDate >= startDate && normalizedPolicyDate <= endDate;
        } catch (err) {
          console.warn('Error filtering by date:', p.id, err);
          return false;
        }
      });
    }
    
    // Apply commission filter
    if (commissionFilter === 'with') {
      filtered = filtered.filter(p => p.commission_of_policy && p.commission_of_policy.length > 0);
    } else if (commissionFilter === 'without') {
      filtered = filtered.filter(p => !p.commission_of_policy || p.commission_of_policy.length === 0);
    }
    
    // Apply status filter
    if (selectedStatuses.length > 0) {
      filtered = filtered.filter(p => selectedStatuses.includes(p.status_name));
    }
    
    const agentsMap = new Map();
    filtered.forEach(p => {
      if (p.agentOwner_name) {
        const key = `${p.agentOwner_name}|${p.agentOwner_npn || ''}`;
        if (!agentsMap.has(key)) {
          agentsMap.set(key, {
            name: p.agentOwner_name,
            npn: p.agentOwner_npn || 'N/A'
          });
        }
      }
    });
    return Array.from(agentsMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [policies, policySearchQuery, policyDateRange, dateFilterType, commissionFilter, selectedStatuses]);

  // Filtered policies
  const filteredPolicies = useMemo(() => {
    let filtered = [...policies];
    
    // Search filter - supports comma-separated values
    if (policySearchQuery) {
      const query = policySearchQuery.trim();
      
      // Check if query contains comma (multiple values)
      if (query.includes(',')) {
        // Split by comma, trim whitespace, and filter empty values
        const searchValues = query.split(',')
          .map(val => val.trim().toLowerCase())
          .filter(val => val.length > 0);
        
        // Filter policies that match ANY of the search values
        filtered = filtered.filter(p => {
          const clientLower = p.client?.toLowerCase() || '';
          const policyNumberLower = p.policy_number?.toLowerCase() || '';
          const agentNameLower = p.agentOwner_name?.toLowerCase() || '';
          const agentNpn = String(p.agentOwner_npn || '').toLowerCase();
          
          return searchValues.some(searchVal => 
            clientLower.includes(searchVal) ||
            policyNumberLower.includes(searchVal) ||
            agentNameLower.includes(searchVal) ||
            agentNpn === searchVal || // Exact match for NPN
            agentNpn.includes(searchVal) // Partial match for NPN
          );
        });
      } else {
        // Single value search (original behavior)
        const queryLower = query.toLowerCase();
        filtered = filtered.filter(p => 
          p.client?.toLowerCase().includes(queryLower) ||
          p.policy_number?.toLowerCase().includes(queryLower) ||
          p.agentOwner_name?.toLowerCase().includes(queryLower) ||
          String(p.agentOwner_npn || '').toLowerCase().includes(queryLower)
        );
      }
    }
    
    // Date filter - Filter by Effective Date or Created Date based on dateFilterType
    if (policyDateRange && policyDateRange.from && policyDateRange.to) {
      filtered = filtered.filter(p => {
        try {
          let policyDate;
          
          if (dateFilterType === 'effective') {
            // Use raw initial_draft_date for filtering
            if (!p.initial_draft_date_raw && !p.initial_draft_date) return false;
            const dateStr = p.initial_draft_date_raw || p.initial_draft_date;
            if (!dateStr || typeof dateStr !== 'string') return false;
            
            // Parse the initial_draft_date (format: YYYY-MM-DD) explicitly to avoid timezone issues
            const parts = dateStr.split('-');
            if (parts.length !== 3) return false;
            
            const [year, month, day] = parts.map(Number);
            if (isNaN(year) || isNaN(month) || isNaN(day)) return false;
            
            policyDate = new Date(year, month - 1, day); // month is 0-indexed
          } else {
            // Use created_timestamp for created date filter
            if (!p.created_timestamp) return false;
            const timestamp = p.created_timestamp < 10000000000 ? p.created_timestamp * 1000 : p.created_timestamp;
            policyDate = new Date(timestamp);
          }
          
          // Validate the date
          if (isNaN(policyDate.getTime())) return false;
          
          const normalizedPolicyDate = new Date(policyDate);
          normalizedPolicyDate.setHours(0, 0, 0, 0);
          
          const startDate = new Date(policyDateRange.from);
          startDate.setHours(0, 0, 0, 0);
          
          const endDate = new Date(policyDateRange.to);
          endDate.setHours(23, 59, 59, 999);
          
          return normalizedPolicyDate >= startDate && normalizedPolicyDate <= endDate;
        } catch (err) {
          console.warn('Error filtering policy by date:', p.id, err);
          return false;
        }
      });
    }
    
    // Commission filter (with/without)
    if (commissionFilter === 'with') {
      filtered = filtered.filter(p => p.commission_of_policy && p.commission_of_policy.length > 0);
    } else if (commissionFilter === 'without') {
      filtered = filtered.filter(p => !p.commission_of_policy || p.commission_of_policy.length === 0);
    }
    
    // Status filter (multi-select)
    if (selectedStatuses.length > 0) {
      filtered = filtered.filter(p => selectedStatuses.includes(p.status_name));
    }
    
    // Agent filter (multi-select)
    if (selectedAgents.length > 0) {
      filtered = filtered.filter(p => {
        const agentKey = `${p.agentOwner_name}|${p.agentOwner_npn || ''}`;
        return selectedAgents.includes(agentKey);
      });
    }
    
    return filtered;
  }, [policies, policySearchQuery, policyDateRange, dateFilterType, commissionFilter, selectedStatuses, selectedAgents]);

  // All commissions from all policies
  const allCommissions = useMemo(() => {
    const commissions = [];
    policies.forEach(policy => {
      const policyCommissions = policy.commission_of_policy || [];
      policyCommissions.forEach(commission => {
        let formattedDate = 'N/A';
        if (commission.created_at) {
          try {
            const timestamp = commission.created_at < 10000000000 ? commission.created_at * 1000 : commission.created_at;
            const date = new Date(timestamp);
            if (!isNaN(date.getTime())) {
              formattedDate = format(date, 'MMM dd, yyyy');
            }
          } catch (err) {
            console.warn('Invalid date in commission:', commission.created_at);
            formattedDate = 'N/A';
          }
        }
        
        commissions.push({
          ...commission,
          policy_number: policy.policy_number,
          policy_client: policy.client,
          agentOwner_name: policy.agentOwner_name,
          agentOwner_npn: policy.agentOwner_npn,
          policy_id: policy.id,
          created_date: formattedDate,
          created_timestamp: commission.created_at,
          agentonCommission_name: commission.agentonCommission_name,
          submittedBy_name: commission.submittedBy_name
        });
      });
    });
    return commissions;
  }, [policies]);

  // Filtered commissions - based on filtered policies (cascade filtering)
  const filteredCommissions = useMemo(() => {
    // Start with commissions from filtered policies only
    const filteredPolicyIds = new Set(filteredPolicies.map(p => p.id));
    let filtered = allCommissions.filter(c => filteredPolicyIds.has(c.policy_id));
    
    // Filter by selected policy
    if (selectedPolicyId) {
      filtered = filtered.filter(c => c.policy_id === selectedPolicyId);
    }
    
    // Search filter
    if (commissionSearchQuery) {
      const query = commissionSearchQuery.toLowerCase();
      filtered = filtered.filter(c => 
        c.policy_number?.toLowerCase().includes(query) ||
        c.agentOwner_name?.toLowerCase().includes(query) ||
        String(c.agentOwner_npn || '').toLowerCase().includes(query) ||
        c.agentonCommission_name?.toLowerCase().includes(query) ||
        c.comment?.toLowerCase().includes(query) ||
        c.submittedBy_name?.toLowerCase().includes(query)
      );
    }
    
    // Paid Split filter (multi-select)
    if (selectedPaidSplitStatuses.length > 0) {
      filtered = filtered.filter(c => {
        const status = c.paid_split || 'N/A';
        return selectedPaidSplitStatuses.some(selectedStatus => {
          if (selectedStatus === 'paid') {
            return status.toLowerCase() === 'paid';
          } else if (selectedStatus === 'unpaid') {
            return status.toLowerCase() === 'unpaid';
          } else if (selectedStatus === 'n/a') {
            return status === 'N/A' || !status;
          } else if (selectedStatus === 'chargeback') {
            return status.toLowerCase() === 'chargeback';
          }
          return false;
        });
      });
    }
    
    return filtered;
  }, [allCommissions, filteredPolicies, selectedPolicyId, commissionSearchQuery, selectedPaidSplitStatuses]);

  // Pagination for policies
  const paginatedPolicies = useMemo(() => {
    const startIndex = (policyPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredPolicies.slice(startIndex, endIndex);
  }, [filteredPolicies, policyPage, itemsPerPage]);

  const totalPolicyPages = Math.ceil(filteredPolicies.length / itemsPerPage);

  // Pagination for commissions
  const paginatedCommissions = useMemo(() => {
    const startIndex = (commissionPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredCommissions.slice(startIndex, endIndex);
  }, [filteredCommissions, commissionPage, itemsPerPage]);

  const totalCommissionPages = Math.ceil(filteredCommissions.length / itemsPerPage);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPolicyPage(1);
  }, [policySearchQuery, policyDateRange, commissionFilter, selectedStatuses, selectedAgencies, selectedCarrier]);

  useEffect(() => {
    setCommissionPage(1);
  }, [commissionSearchQuery, selectedPaidSplitStatuses, selectedPolicyId]);

  // Dashboard stats
  const dashboardStats = useMemo(() => {
    const totalPolicies = filteredPolicies.length;
    let totalAnnualPremium = 0;
    let approved = { count: 0, premium: 0 };
    let underwriting = { count: 0, premium: 0 };
    let followUp = { count: 0, premium: 0 };
    let cancelledBeforeDraft = { count: 0, premium: 0 };
    let declined = { count: 0, premium: 0 };
    let lapsedPending = { count: 0, premium: 0 };
    let lapsed = { count: 0, premium: 0 };
    let notTaken = { count: 0, premium: 0 };
    const totalCommissions = filteredCommissions.length;

    filteredPolicies.forEach(policy => {
      const premium = parseFloat(policy.annual_premium) || 0;
      totalAnnualPremium += premium;
      
      const status = (policy.status_name || '').toLowerCase();
      
      if (status.includes('approved') || status.includes('active')) {
        approved.count++;
        approved.premium += premium;
      } else if (status.includes('underwriting')) {
        underwriting.count++;
        underwriting.premium += premium;
      } else if (status.includes('follow')) {
        followUp.count++;
        followUp.premium += premium;
      } else if (status.includes('cancelled before draft')) {
        cancelledBeforeDraft.count++;
        cancelledBeforeDraft.premium += premium;
      } else if (status.includes('declined')) {
        declined.count++;
        declined.premium += premium;
      } else if (status.includes('lapsed pending')) {
        lapsedPending.count++;
        lapsedPending.premium += premium;
      } else if (status === 'lapsed') {
        lapsed.count++;
        lapsed.premium += premium;
      } else if (status.includes('not taken')) {
        notTaken.count++;
        notTaken.premium += premium;
      }
    });
    
    return {
      totalPolicies,
      totalAnnualPremium,
      approved,
      underwriting,
      followUp,
      cancelledBeforeDraft,
      declined,
      lapsedPending,
      lapsed,
      notTaken,
      totalCommissions
    };
  }, [filteredPolicies, filteredCommissions]);

  // Handler for dashboard card clicks to filter
  const handleDashboardFilter = (filterType) => {
    // Get the base policies (before status filter) to find matching statuses
    const allStatuses = Array.from(new Set(policies.map(p => p.status_name).filter(Boolean)));
    
    switch(filterType) {
      case 'total':
        // Clear only status filter to show all (keep other filters)
        setSelectedStatuses([]);
        break;
      case 'approved':
        // Filter to approved/active policies
        const approvedStatuses = allStatuses.filter(s => 
          s.toLowerCase().includes('approved') || s.toLowerCase().includes('active')
        );
        setSelectedStatuses(approvedStatuses);
        break;
      case 'underwriting':
        // Filter to underwriting policies
        const underwritingStatuses = allStatuses.filter(s => 
          s.toLowerCase().includes('underwriting')
        );
        setSelectedStatuses(underwritingStatuses);
        break;
      case 'cancelled':
        // Filter to cancelled before draft
        const cancelledStatuses = allStatuses.filter(s => 
          s.toLowerCase().includes('cancelled before draft')
        );
        setSelectedStatuses(cancelledStatuses);
        break;
      case 'declined':
        // Filter to declined
        const declinedStatuses = allStatuses.filter(s => 
          s.toLowerCase().includes('declined')
        );
        setSelectedStatuses(declinedStatuses);
        break;
      case 'lapsed':
        // Filter to lapsed (but not lapsed pending)
        const lapsedStatuses = allStatuses.filter(s => 
          s.toLowerCase() === 'lapsed'
        );
        setSelectedStatuses(lapsedStatuses);
        break;
      case 'notTaken':
        // Filter to not taken
        const notTakenStatuses = allStatuses.filter(s => 
          s.toLowerCase().includes('not taken')
        );
        setSelectedStatuses(notTakenStatuses);
        break;
      default:
        break;
    }
  };

  if (!userInfo?.hasCommissionPermission) {
    return (
      <div className="space-y-4">
        <div>
          <h2 className="text-3xl font-bold text-neutral-800">Commission Tracker</h2>
          <p className="text-neutral-600 mt-1">Track and manage agency commissions</p>
        </div>
        
        <Card className="rounded-2xl border-red-300">
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <p className="text-red-600 font-medium">Access Denied</p>
              <p className="text-neutral-500 mt-2">You don&apos;t have permission to view this page.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!fflAgencyAccess || fflAgencyAccess.length === 0) {
    return (
      <div className="space-y-4">
        <div>
          <h2 className="text-3xl font-bold text-neutral-800">Commission Tracker</h2>
          <p className="text-neutral-600 mt-1">Track and manage agency commissions</p>
        </div>
        
        <div className="flex flex-col items-center justify-center h-64 space-y-4">
          <Building className="w-16 h-16 text-neutral-300" />
          <p className="text-neutral-500 text-lg">No Agency Access Found</p>
          <p className="text-neutral-400 text-sm">You don&apos;t have access to any agencies yet</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-neutral-800 dark:text-white">Commission Tracker</h2>
          <p className="text-neutral-600 dark:text-neutral-400 mt-1">Track and manage agency commissions</p>
        </div>
        <Badge className="bg-[#d4af37] text-white text-lg px-4 py-2">
          {fflAgencyAccess.length} {fflAgencyAccess.length === 1 ? 'Agency' : 'Agencies'}
        </Badge>
      </div>

      {/* Dashboard Stats - Modern Design */}
      <div className="grid grid-cols-4 gap-6 mt-6">
        {/* Total Policies - Hero Card */}
        <button 
          onClick={() => handleDashboardFilter('total')}
          className="relative overflow-hidden bg-gradient-to-br from-[#d4af37] via-[#f4e5b8] to-[#fffef0] dark:from-[#d4af37]/20 dark:via-[#d4af37]/10 dark:to-neutral-800 p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-[#d4af37]/20 dark:border-[#d4af37]/40 text-left cursor-pointer hover:scale-105"
        >
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">Total Policies</p>
              <TrendingUp className="w-5 h-5 text-[#b8941e] dark:text-[#d4af37]" />
            </div>
            <p className="text-4xl font-black text-[#b8941e] dark:text-[#d4af37] mb-1">{dashboardStats.totalPolicies}</p>
            <p className="text-xs text-neutral-600 dark:text-neutral-400 font-medium">Click to show all</p>
          </div>
          <div className="absolute -right-4 -bottom-4 opacity-10">
            <div className="w-24 h-24 bg-[#d4af37] rounded-full"></div>
          </div>
        </button>

        {/* Approved */}
        <button 
          onClick={() => handleDashboardFilter('approved')}
          className="relative overflow-hidden bg-gradient-to-br from-emerald-400 via-green-300 to-green-100 dark:from-emerald-600/20 dark:via-emerald-700/10 dark:to-neutral-800 p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 dark:border dark:border-emerald-600/40 text-left cursor-pointer hover:scale-105"
        >
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-green-900 dark:text-emerald-300 uppercase tracking-wider">Approved</p>
              <CheckCircle2 className="w-5 h-5 text-green-700 dark:text-emerald-400" />
            </div>
            <p className="text-3xl font-black text-green-900 dark:text-emerald-400 mb-1">{dashboardStats.approved.count}</p>
            <p className="text-sm font-bold text-green-700 dark:text-emerald-500">${dashboardStats.approved.premium.toLocaleString()}</p>
          </div>
          <div className="absolute -right-6 -bottom-6 opacity-10">
            <CheckCircle2 className="w-32 h-32 text-green-900 dark:text-emerald-400" />
          </div>
        </button>

        {/* Underwriting */}
        <button 
          onClick={() => handleDashboardFilter('underwriting')}
          className="relative overflow-hidden bg-gradient-to-br from-blue-400 via-blue-300 to-blue-100 dark:from-blue-600/20 dark:via-blue-700/10 dark:to-neutral-800 p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 dark:border dark:border-blue-600/40 text-left cursor-pointer hover:scale-105"
        >
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-blue-900 dark:text-blue-300 uppercase tracking-wider">Underwriting</p>
              <Clock className="w-5 h-5 text-blue-700 dark:text-blue-400" />
            </div>
            <p className="text-3xl font-black text-blue-900 dark:text-blue-400 mb-1">{dashboardStats.underwriting.count}</p>
            <p className="text-sm font-bold text-blue-700 dark:text-blue-500">${dashboardStats.underwriting.premium.toLocaleString()}</p>
          </div>
          <div className="absolute -right-6 -bottom-6 opacity-10">
            <Clock className="w-32 h-32 text-blue-900 dark:text-blue-400" />
          </div>
        </button>

        {/* Total Premium */}
        <div className="relative overflow-hidden bg-gradient-to-br from-amber-400 via-yellow-300 to-amber-100 dark:from-amber-600/20 dark:via-amber-700/10 dark:to-neutral-800 p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 dark:border dark:border-amber-600/40">
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-amber-900 dark:text-amber-300 uppercase tracking-wider">Total Premium</p>
              <DollarSign className="w-5 h-5 text-amber-700 dark:text-amber-400" />
            </div>
            <p className="text-2xl font-black text-amber-900 dark:text-amber-400 mb-1">${dashboardStats.totalAnnualPremium.toLocaleString()}</p>
            <p className="text-xs text-amber-700 dark:text-amber-500 font-medium">Annual Revenue</p>
          </div>
          <div className="absolute -right-6 -bottom-6 opacity-10">
            <DollarSign className="w-32 h-32 text-amber-900 dark:text-amber-400" />
          </div>
        </div>
      </div>

      {/* Dashboard Stats - Row 2 - Compact Status Cards */}
      <div className="grid grid-cols-5 gap-4 mt-4">
        <button 
          onClick={() => handleDashboardFilter('cancelled')}
          className="bg-white dark:bg-neutral-800 border-2 border-red-200 dark:border-red-900/50 p-4 rounded-xl hover:border-red-400 dark:hover:border-red-700 transition-all duration-200 hover:shadow-md text-left cursor-pointer hover:scale-105"
        >
          <p className="text-2xl font-black text-red-600 dark:text-red-400">{dashboardStats.cancelledBeforeDraft.count}</p>
          <p className="text-xs text-red-700 dark:text-red-500 font-semibold mt-1">Cancelled</p>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">${dashboardStats.cancelledBeforeDraft.premium.toLocaleString()}</p>
        </button>

        <button 
          onClick={() => handleDashboardFilter('declined')}
          className="bg-white dark:bg-neutral-800 border-2 border-orange-200 dark:border-orange-900/50 p-4 rounded-xl hover:border-orange-400 dark:hover:border-orange-700 transition-all duration-200 hover:shadow-md text-left cursor-pointer hover:scale-105"
        >
          <p className="text-2xl font-black text-orange-600 dark:text-orange-400">{dashboardStats.declined.count}</p>
          <p className="text-xs text-orange-700 dark:text-orange-500 font-semibold mt-1">Declined</p>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">${dashboardStats.declined.premium.toLocaleString()}</p>
        </button>

        <button 
          onClick={() => handleDashboardFilter('lapsed')}
          className="bg-white dark:bg-neutral-800 border-2 border-purple-200 dark:border-purple-900/50 p-4 rounded-xl hover:border-purple-400 dark:hover:border-purple-700 transition-all duration-200 hover:shadow-md text-left cursor-pointer hover:scale-105"
        >
          <p className="text-2xl font-black text-purple-600 dark:text-purple-400">{dashboardStats.lapsed.count}</p>
          <p className="text-xs text-purple-700 dark:text-purple-500 font-semibold mt-1">Lapsed</p>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">${dashboardStats.lapsed.premium.toLocaleString()}</p>
        </button>

        <button 
          onClick={() => handleDashboardFilter('notTaken')}
          className="bg-white dark:bg-neutral-800 border-2 border-pink-200 dark:border-pink-900/50 p-4 rounded-xl hover:border-pink-400 dark:hover:border-pink-700 transition-all duration-200 hover:shadow-md text-left cursor-pointer hover:scale-105"
        >
          <p className="text-2xl font-black text-pink-600 dark:text-pink-400">{dashboardStats.notTaken.count}</p>
          <p className="text-xs text-pink-700 dark:text-pink-500 font-semibold mt-1">Not Taken</p>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">${dashboardStats.notTaken.premium.toLocaleString()}</p>
        </button>
      </div>

      {/* Results Section - Two Tables */}
      {loadingPolicies ? (
        <Card className="rounded-2xl">
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-[#d4af37] mx-auto mb-4" />
              <p className="text-neutral-600">Loading commission policies...</p>
            </div>
          </CardContent>
        </Card>
      ) : policies.length === 0 ? (
        <Card className="rounded-2xl">
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <Building className="w-12 h-12 text-neutral-300 mx-auto mb-2" />
              <p className="text-neutral-500">No commission policies found</p>
              <p className="text-sm text-neutral-400 mt-1">Try selecting different filters</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {/* Policies Table - Full Width */}
          <div className="lg:col-span-12">
            <Card className="rounded-2xl dark:bg-neutral-800 dark:border-neutral-700">
              <CardContent className="pt-6">
                <div className="space-y-3">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-neutral-700 dark:text-neutral-200 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-[#d4af37]" />
                      Policies
                    </h3>
                    <Badge className="bg-[#d4af37] text-white">
                      {filteredPolicies.length}
                    </Badge>
                  </div>

                  {/* Agency and Carrier Selection */}
                  <div className="mb-4 p-4 bg-neutral-50 dark:bg-neutral-900 rounded-xl">
                    <div className="flex items-end gap-3">
                      {/* Agency Dropdown with Search */}
                      <div className="flex-1 space-y-2">
                        <Label className="text-sm font-medium text-neutral-700">
                          Agency <span className="text-red-500">*</span>
                        </Label>
                        <Popover open={agencyOpen} onOpenChange={setAgencyOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              aria-expanded={agencyOpen}
                              className="w-full justify-between rounded-xl h-10"
                            >
                              {selectedAgencies.length === 0
                                ? "Select Agencies"
                                : selectedAgencies.length === fflAgencyAccess.length
                                ? "All Agencies"
                                : `${selectedAgencies.length} selected`}
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-full p-0">
                            <div className="flex flex-col">
                    <div className="flex items-center border-b px-3">
                      <svg className="mr-2 h-4 w-4 shrink-0 opacity-50" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <input
                        className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-neutral-500"
                        placeholder="Search agency..."
                      />
                    </div>
                    <div className="max-h-64 overflow-auto p-1">
                                {fflAgencyAccess.map((agency) => (
                                  <div
                                    key={agency.id}
                                    className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none hover:bg-neutral-100 active:bg-neutral-200"
                                    onClick={() => {
                                      setSelectedAgencies(prev =>
                                        prev.includes(agency.id)
                                          ? prev.filter(id => id !== agency.id)
                                          : [...prev, agency.id]
                                      );
                                    }}
                                  >
                                    <Checkbox
                                      checked={selectedAgencies.includes(agency.id)}
                                      className="mr-2"
                                    />
                                    {agency.name}
                                  </div>
                                ))}
                              </div>
                  </div>
                          </PopoverContent>
                        </Popover>
                      </div>

                      {/* Carrier Dropdown with Search */}
                      <div className="flex-1 space-y-2">
                        <Label className="text-sm font-medium text-neutral-700">
                          Carrier
                        </Label>
                        {loadingCarriers ? (
                          <div className="flex items-center justify-center h-10 border border-neutral-300 rounded-xl">
                            <Loader2 className="w-4 h-4 animate-spin text-[#d4af37]" />
                          </div>
                        ) : (
                          <Popover open={carrierOpen} onOpenChange={setCarrierOpen}>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                role="combobox"
                                aria-expanded={carrierOpen}
                                className="w-full justify-between rounded-xl h-10"
                              >
                                {selectedCarrier
                                  ? carriers.find((carrier) => carrier.id === selectedCarrier)?.name
                                  : "Select Carrier (Optional)"}
                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-full p-0">
                              <div className="flex flex-col">
                    <div className="flex items-center border-b px-3">
                      <svg className="mr-2 h-4 w-4 shrink-0 opacity-50" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <input
                        className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-neutral-500"
                        placeholder="Search carrier..."
                      />
                    </div>
                    <div className="max-h-64 overflow-auto p-1">
                                  {carriers.map((carrier) => (
                                    <div
                                      key={carrier.id}
                                      className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none hover:bg-neutral-100 active:bg-neutral-200"
                                      onClick={() => {
                                        setSelectedCarrier(carrier.id);
                                        setCarrierOpen(false);
                                      }}
                                    >
                                      <Check
                                        className={cn(
                                          "mr-2 h-4 w-4",
                                          selectedCarrier === carrier.id ? "opacity-100" : "opacity-0"
                                        )}
                                      />
                                      {carrier.name}
                                    </div>
                                  ))}
                                </div>
                  </div>
                            </PopoverContent>
                          </Popover>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-2 pb-[2px]">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={handleReset}
                                className="rounded-xl h-10 w-10"
                              >
                                <RefreshCw className="w-4 h-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Reset</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <Button
                          onClick={handleSearch}
                          disabled={selectedAgencies.length === 0}
                          className="bg-[#d4af37] hover:bg-[#b8941e] text-white rounded-xl h-10 px-4"
                        >
                          <Search className="w-4 h-4 mr-2" />
                          Search Commission
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Filters */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      {/* Filter by Status - Icon Button */}
                      {uniqueStatuses.length > 0 && (
                        <TooltipProvider>
                          <Tooltip>
                            <Popover>
                              <PopoverTrigger asChild>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    className={cn(
                                      "rounded-xl h-10 w-10 relative",
                                      selectedStatuses.length > 0 && "border-[#d4af37] bg-[#d4af37]/10"
                                    )}
                                  >
                                    <FileText className="w-4 h-4" />
                                    {selectedStatuses.length > 0 && (
                                      <span className="absolute -top-1 -right-1 bg-[#d4af37] text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
                                        {selectedStatuses.length}
                                      </span>
                                    )}
                                  </Button>
                                </TooltipTrigger>
                              </PopoverTrigger>
                              <PopoverContent className="w-64 p-0" align="start">
                                <div className="flex flex-col">
                                  <div className="flex items-center border-b px-3">
                                    <svg className="mr-2 h-4 w-4 shrink-0 opacity-50" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                    <input
                                      className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-neutral-500"
                                      placeholder="Search status..."
                                    />
                                  </div>
                                  <div className="max-h-64 overflow-auto p-1">
                                    {uniqueStatuses.map((status) => (
                                      <div
                                        key={status}
                                        className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none hover:bg-neutral-100 active:bg-neutral-200"
                                        onClick={() => {
                                          setSelectedStatuses(prev =>
                                            prev.includes(status)
                                              ? prev.filter(s => s !== status)
                                              : [...prev, status]
                                          );
                                        }}
                                      >
                                        <Checkbox
                                          checked={selectedStatuses.includes(status)}
                                          className="mr-2"
                                        />
                                        {status}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </PopoverContent>
                            </Popover>
                            <TooltipContent>
                              <p>Filter by Status</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      
                      {/* Filter by Commission - Icon Button */}
                      <TooltipProvider>
                        <Tooltip>
                          <Popover>
                            <PopoverTrigger asChild>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="icon"
                                  className={cn(
                                    "rounded-xl h-10 w-10",
                                    commissionFilter !== 'all' && "border-[#d4af37] bg-[#d4af37]/10"
                                  )}
                                >
                                  <DollarSign className="w-4 h-4" />
                                </Button>
                              </TooltipTrigger>
                            </PopoverTrigger>
                            <PopoverContent className="w-56 p-3" align="start">
                              <div className="space-y-2">
                                <p className="text-sm font-medium text-neutral-700 mb-2">Commission Filter</p>
                                <Select value={commissionFilter} onValueChange={setCommissionFilter}>
                                  <SelectTrigger className="rounded-xl border-neutral-300">
                                    <SelectValue placeholder="All Policies" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="all">All Policies</SelectItem>
                                    <SelectItem value="with">With Commission</SelectItem>
                                    <SelectItem value="without">Without Commission</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </PopoverContent>
                          </Popover>
                          <TooltipContent>
                            <p>Filter by Commission</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      
                      {/* Dynamic Date Filter - Icon Button */}
                      <TooltipProvider>
                        <Tooltip>
                          <Popover>
                            <PopoverTrigger asChild>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="icon"
                                  className={cn(
                                    "rounded-xl h-10 w-10",
                                    policyDateRange && "border-[#d4af37] bg-[#d4af37]/10"
                                  )}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                    <line x1="16" y1="2" x2="16" y2="6"></line>
                                    <line x1="8" y1="2" x2="8" y2="6"></line>
                                    <line x1="3" y1="10" x2="21" y2="10"></line>
                                  </svg>
                                </Button>
                              </TooltipTrigger>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-4" align="start">
                              <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                  <p className="text-sm font-medium text-neutral-700">Date Filter</p>
                                  {policyDateRange && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => setPolicyDateRange(null)}
                                      className="h-6 text-xs"
                                    >
                                      Clear
                                    </Button>
                                  )}
                                </div>
                                <Select value={dateFilterType} onValueChange={setDateFilterType}>
                                  <SelectTrigger className="rounded-xl border-neutral-300">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="effective">Filter by Effective Date</SelectItem>
                                    <SelectItem value="created">Filter by Date Created</SelectItem>
                                  </SelectContent>
                                </Select>
                                <DateRangePicker value={policyDateRange} onChange={setPolicyDateRange} />
                              </div>
                            </PopoverContent>
                          </Popover>
                          <TooltipContent>
                            <p>Filter by Date</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      
                      {/* Filter by Agent on Record - Icon Button */}
                      {uniqueAgents.length > 0 && (
                        <TooltipProvider>
                          <Tooltip>
                            <Popover>
                              <PopoverTrigger asChild>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    className={cn(
                                      "rounded-xl h-10 w-10 relative",
                                      selectedAgents.length > 0 && "border-[#d4af37] bg-[#d4af37]/10"
                                    )}
                                  >
                                    <Users className="w-4 h-4" />
                                    {selectedAgents.length > 0 && (
                                      <span className="absolute -top-1 -right-1 bg-[#d4af37] text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
                                        {selectedAgents.length}
                                      </span>
                                    )}
                                  </Button>
                                </TooltipTrigger>
                              </PopoverTrigger>
                              <PopoverContent className="w-80 p-0" align="start">
                                <div className="flex flex-col">
                    <div className="flex items-center border-b px-3">
                      <svg className="mr-2 h-4 w-4 shrink-0 opacity-50" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <input
                        className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-neutral-500"
                        placeholder="Search agent..."
                      />
                    </div>
                    <div className="max-h-64 overflow-auto p-1">
                                    {uniqueAgents.map((agent) => {
                                      const agentKey = `${agent.name}|${agent.npn}`;
                                      return (
                                        <div
                                          key={agentKey}
                                          className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none hover:bg-neutral-100 active:bg-neutral-200"
                                          onClick={() => {
                                            setSelectedAgents(prev =>
                                              prev.includes(agentKey)
                                                ? prev.filter(a => a !== agentKey)
                                                : [...prev, agentKey]
                                            );
                                          }}
                                        >
                                          <Checkbox
                                            checked={selectedAgents.includes(agentKey)}
                                            className="mr-2"
                                          />
                                          <div>
                                            <p className="text-sm font-medium">{agent.name}</p>
                                            <p className="text-xs text-neutral-500">NPN: {agent.npn}</p>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                  </div>
                              </PopoverContent>
                            </Popover>
                            <TooltipContent>
                              <p>Filter by Agent on Record</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      
                      {/* Quick Search - Expandable Icon (Last Position) */}
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className={cn("flex items-center transition-all duration-300", policySearchExpanded ? "flex-1" : "")}>
                              {!policySearchExpanded ? (
                                <Button
                                  variant="outline"
                                  size="icon"
                                  onClick={() => setPolicySearchExpanded(true)}
                                  className="rounded-xl h-10 w-10"
                                >
                                  <Search className="w-4 h-4" />
                                </Button>
                              ) : (
                                <div className="relative flex-1">
                                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400" />
                                  <Input
                                    placeholder="Search policies... (use commas for multiple NPNs)"
                                    value={policySearchQuery}
                                    onChange={(e) => setPolicySearchQuery(e.target.value)}
                                    onBlur={() => {
                                      if (!policySearchQuery) setPolicySearchExpanded(false);
                                    }}
                                    autoFocus
                                    className="pl-10 pr-10 rounded-xl border-neutral-300 h-10"
                                  />
                                  {policySearchQuery && (
                                    <button
                                      onClick={() => {
                                        setPolicySearchQuery('');
                                        setPolicySearchExpanded(false);
                                      }}
                                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                                    >
                                      ✕
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          </TooltipTrigger>
                          {!policySearchExpanded && (
                            <TooltipContent>
                              <p>Quick Search</p>
                            </TooltipContent>
                          )}
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    
                    {/* Clear All Filters Button */}
                    {(selectedStatuses.length > 0 || commissionFilter !== 'all' || policyDateRange || selectedAgents.length > 0 || policySearchQuery) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedStatuses([]);
                          setCommissionFilter('all');
                          setPolicyDateRange(null);
                          setSelectedAgents([]);
                          setPolicySearchQuery('');
                          setPolicySearchExpanded(false);
                        }}
                        className="mt-3 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white border-none rounded-xl shadow-md hover:shadow-lg transition-all duration-200"
                      >
                        <RefreshCw className="w-3.5 h-3.5 mr-2" />
                        Clear All Filters
                      </Button>
                    )}
                  </div>

                  {/* Pagination Info */}
                  <div className="flex items-center justify-between text-sm text-neutral-600 mb-2">
                    <div className="flex items-center gap-3">
                      <span>
                        Showing {filteredPolicies.length === 0 ? 0 : (policyPage - 1) * itemsPerPage + 1} - {Math.min(policyPage * itemsPerPage, filteredPolicies.length)} of {filteredPolicies.length}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-neutral-500">Rows per page:</span>
                        <Select 
                          value={itemsPerPage.toString()} 
                          onValueChange={(value) => {
                            setItemsPerPage(Number(value));
                            setPolicyPage(1); // Reset to first page when changing page size
                            setCommissionPage(1); // Reset commission page too
                          }}
                        >
                          <SelectTrigger className="w-20 h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="20">20</SelectItem>
                            <SelectItem value="50">50</SelectItem>
                            <SelectItem value="100">100</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {totalPolicyPages > 1 && <span>Page {policyPage} of {totalPolicyPages}</span>}
                  </div>

                  {/* Policies Table */}
                  <div className="max-h-[600px] overflow-y-auto rounded-xl border border-neutral-200 dark:border-neutral-700">
                    <table className="w-full">
                      <thead className="bg-white dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700 sticky top-0 z-10">
                        <tr>
                          <th className="text-left text-xs font-semibold text-neutral-600 dark:text-neutral-300 px-3 py-3 first:rounded-tl-xl">Created</th>
                          <th className="text-left text-xs font-semibold text-neutral-600 dark:text-neutral-300 px-3 py-3">Agent on Record</th>
                          <th className="text-left text-xs font-semibold text-neutral-600 dark:text-neutral-300 px-3 py-3">Policy #</th>
                          <th className="text-left text-xs font-semibold text-neutral-600 dark:text-neutral-300 px-3 py-3">Effective</th>
                          <th className="text-left text-xs font-semibold text-neutral-600 dark:text-neutral-300 px-3 py-3">Status</th>
                          <th className="text-center text-xs font-semibold text-neutral-600 dark:text-neutral-300 px-3 py-3 last:rounded-tr-xl">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-neutral-100 dark:divide-neutral-700 bg-white dark:bg-neutral-900">
                        {paginatedPolicies.map((policy, index) => (
                          <>
                            <tr 
                              key={policy.id || index}
                              id={`policy-${policy.id}`}
                              className={cn(
                                "hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-all duration-200 cursor-pointer group",
                                selectedPolicyId === policy.id && "bg-[#fffef0] dark:bg-[#d4af37]/10 border-l-2 border-l-[#d4af37]",
                                expandedPolicies[policy.id] && "bg-neutral-50 dark:bg-neutral-800"
                              )}
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePolicyClick(policy);
                              }}
                            >
                              <td className="px-3 py-3 first:pl-4">
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      togglePolicy(policy.id);
                                    }}
                                    className="p-1 hover:bg-[#d4af37]/10 rounded-md transition-colors"
                                  >
                                    {expandedPolicies[policy.id] ? (
                                      <ChevronDown className="w-3.5 h-3.5 text-[#d4af37]" />
                                    ) : (
                                      <ChevronRight className="w-3.5 h-3.5 text-neutral-400 group-hover:text-[#d4af37]" />
                                    )}
                                  </button>
                                  <span className="text-sm text-neutral-600 dark:text-neutral-300">{policy.created_date}</span>
                                </div>
                              </td>
                              <td className="px-3 py-3">
                                <div>
                                  <p className="text-sm font-medium text-neutral-800 dark:text-neutral-200">{policy.agentOwner_name || 'N/A'}</p>
                                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">{policy.agentOwner_npn || 'N/A'}</p>
                                </div>
                              </td>
                              <td className="px-3 py-3">
                                <span className="text-sm font-medium text-neutral-800 dark:text-neutral-200">#{policy.policy_number || 'N/A'}</span>
                              </td>
                              <td className="px-3 py-3">
                                <span className="text-sm text-neutral-600 dark:text-neutral-300">{policy.effective_date}</span>
                              </td>
                              <td className="px-3 py-3">
                                <div className="flex flex-wrap items-center gap-1.5">
                                  {policy.commission_of_policy.length > 0 && (
                                    <Badge variant="outline" className="border-green-500 text-green-700 text-[10px] bg-green-50 rounded-full font-medium px-2 py-0">
                                      <DollarSign className="w-2.5 h-2.5 mr-0.5" />
                                      {policy.commission_of_policy.length}
                                    </Badge>
                                  )}
                                  {policy.isLocked && (
                                    <Badge variant="outline" className="border-red-400 text-red-600 text-[10px] px-1.5 py-0 bg-red-50 rounded-full">
                                      <Lock className="w-2.5 h-2.5" />
                                    </Badge>
                                  )}
                                  {policy.paid_status && (
                                    <Badge variant="outline" className="border-blue-400 text-blue-700 text-[10px] bg-blue-50 rounded-full font-medium px-2 py-0">
                                      {policy.paid_status}
                                    </Badge>
                                  )}
                                  <Badge className="bg-[#f4e5b8] text-[#b8941e] text-[10px] rounded-full font-medium px-2 py-0">
                                    {policy.status_name}
                                  </Badge>
                                </div>
                              </td>
                              <td className="px-3 py-3 text-center last:pr-4">
                                <div className="flex items-center justify-center gap-1">
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedPolicyId(policy.id);
                                            setShowAddCommissionModal(true);
                                          }}
                                          className="h-8 w-8 p-0 hover:bg-green-100 hover:text-green-600 rounded-md transition-all"
                                          title="Add Commission"
                                        >
                                          <DollarSign className="w-4 h-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>Add Commission</TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditPolicy(policy);
                                    }}
                                    className="h-8 w-8 p-0 hover:bg-[#d4af37]/10 hover:text-[#d4af37] rounded-md transition-all opacity-0 group-hover:opacity-100"
                                    title="Edit Policy"
                                  >
                                    <Edit className="w-3.5 h-3.5" />
                                  </Button>
                                  {!policy.isLocked && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeletePolicy(policy);
                                      }}
                                      className="h-8 w-8 p-0 hover:bg-red-100 hover:text-red-600 rounded-md transition-all opacity-0 group-hover:opacity-100"
                                      title="Delete Policy"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                            {expandedPolicies[policy.id] && (
                              <tr key={`${policy.id}-details`}>
                                <td colSpan="6" className="px-0 py-0">
                                  <div className="border-t border-neutral-200 p-6 bg-gradient-to-b from-neutral-50 to-white space-y-4">
                            {/* Policy Details Table */}
                            <div className="grid grid-cols-2 gap-4">
                              <InfoField label="Client" value={policy.client || 'N/A'} />
                              <InfoField label="Annual Premium" value={`$${policy.annual_premium || 0}`} />
                              <InfoField label="Monthly Premium" value={`$${policy.monthly_premium || 0}`} />
                              <InfoField label="Carrier" value={policy.policyCarrier || policy.carrierName || policy.carrier_name || 'N/A'} />
                              <InfoField label="Product" value={policy.carrier_product || 'N/A'} />
                              <InfoField label="Face Amount" value={policy.face_amount ? `$${policy.face_amount}` : 'N/A'} />
                              <InfoField label="Paid Status" value={policy.paid_status || 'N/A'} />
                            </div>

                            {/* Splits Section */}
                            {policy.split_of_policy && policy.split_of_policy.length > 0 && (
                              <div className="pt-3 border-t border-neutral-300">
                                <div className="flex items-center gap-2 mb-2">
                                  <Users className="w-4 h-4 text-[#d4af37]" />
                                  <h4 className="font-semibold text-neutral-700 text-sm">
                                    Commission Splits ({policy.split_of_policy.length})
                                  </h4>
                                </div>
                                <div className="space-y-2">
                                  {policy.split_of_policy.map((split, idx) => (
                                    <div key={split.id || idx} className="bg-white p-2 rounded-lg border border-neutral-200">
                                      <div className="flex items-center justify-between">
                                        <div>
                                          <p className="text-xs font-medium text-neutral-800">
                                            {split.onsplit_name || 'N/A'}
                                          </p>
                                          <p className="text-xs text-neutral-500">
                                            NPN: {split.onsplit_npn || 'N/A'}
                                          </p>
                                        </div>
                                        <div className="text-right">
                                          <p className="text-sm font-bold text-[#d4af37]">
                                            {split.agent_on_split_percent || 0}%
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                                  </div>
                                </td>
                              </tr>
                            )}
                          </>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Policy Pagination Controls */}
                  {totalPolicyPages > 1 && (
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-neutral-200">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPolicyPage(prev => Math.max(1, prev - 1))}
                        disabled={policyPage === 1}
                        className="rounded-xl"
                      >
                        Previous
                      </Button>
                      
                      <div className="flex items-center gap-2">
                        {Array.from({ length: Math.min(5, totalPolicyPages) }, (_, i) => {
                          let pageNum;
                          if (totalPolicyPages <= 5) {
                            pageNum = i + 1;
                          } else if (policyPage <= 3) {
                            pageNum = i + 1;
                          } else if (policyPage >= totalPolicyPages - 2) {
                            pageNum = totalPolicyPages - 4 + i;
                          } else {
                            pageNum = policyPage - 2 + i;
                          }
                          
                          return (
                            <Button
                              key={pageNum}
                              variant={policyPage === pageNum ? "default" : "outline"}
                              size="sm"
                              onClick={() => setPolicyPage(pageNum)}
                              className={cn(
                                "rounded-xl",
                                policyPage === pageNum && "bg-[#d4af37] hover:bg-[#b8941e] text-white"
                              )}
                            >
                              {pageNum}
                            </Button>
                          );
                        })}
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPolicyPage(prev => Math.min(totalPolicyPages, prev + 1))}
                        disabled={policyPage === totalPolicyPages}
                        className="rounded-xl"
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Edit Policy Dialog */}
      {editingPolicy && (
        <EditCommissionPolicyDialog
          policy={editingPolicy}
          open={showEditDialog}
          onOpenChange={setShowEditDialog}
          onSave={handleSavePolicy}
        />
      )}

      {/* Add Commission Modal */}
      <AddCommissionModal
        policy={getSelectedPolicy()}
        userInfo={userInfo}
        open={showAddCommissionModal}
        onOpenChange={setShowAddCommissionModal}
        onSuccess={handleAddCommissionSuccess}
      />

      {/* Edit Commission Modal */}
      <EditCommissionModal
        commission={editingCommission}
        policy={getSelectedPolicy()}
        userInfo={userInfo}
        open={showEditCommissionModal}
        onOpenChange={setShowEditCommissionModal}
        onSuccess={handleCommissionUpdateSuccess}
      />

      {/* Delete Commission Dialog */}
      <DeleteCommissionDialog
        commission={deletingCommission}
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        onSuccess={handleCommissionUpdateSuccess}
      />

      {/* Delete Policy Confirmation Modal */}
      <Dialog open={showDeletePolicyModal} onOpenChange={(open) => {
        if (!open) {
          setShowDeletePolicyModal(false);
          setDeletingPolicy(null);
          setDeletingPolicyReason('');
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Delete Policy</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this policy? This action is irreversible and cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {deletingPolicy && (
            <div className="space-y-4 py-4">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <p className="text-xs font-semibold text-red-700 mb-3">Policy Details</p>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-neutral-600">Client:</span>
                    <span className="font-medium text-neutral-800">
                      {deletingPolicy.client || 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-600">Policy #:</span>
                    <span className="font-mono font-medium text-neutral-800">
                      {deletingPolicy.policy_number || 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-600">Carrier:</span>
                    <span className="font-medium text-neutral-800">
                      {deletingPolicy.policyCarrier || deletingPolicy.carrierName || deletingPolicy.carrier_name || 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-600">Annual Premium:</span>
                    <span className="font-semibold text-red-600">
                      ${parseFloat(deletingPolicy.annual_premium || 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-neutral-700">
                  Reason for Deletion <span className="text-red-500">*</span>
                </label>
                <Textarea
                  placeholder="Please provide a reason for deleting this policy..."
                  value={deletingPolicyReason}
                  onChange={(e) => setDeletingPolicyReason(e.target.value)}
                  className="min-h-[100px] resize-none"
                  disabled={deletingPolicy_loading}
                />
                {!deletingPolicyReason.trim() && (
                  <p className="text-xs text-neutral-500">This field is required</p>
                )}
              </div>

              <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                <p className="text-xs text-yellow-800 font-medium">
                  ⚠️ Warning: This will permanently delete the policy from the system.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowDeletePolicyModal(false);
                setDeletingPolicy(null);
                setDeletingPolicyReason('');
              }}
              disabled={deletingPolicy_loading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleConfirmDeletePolicy}
              disabled={deletingPolicy_loading || !deletingPolicyReason.trim()}
              variant="destructive"
            >
              {deletingPolicy_loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Confirm Delete'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Agent List Panel */}
      <AgentListPanel />

      {/* Commissions Slide-Out Panel */}
      {showCommissionsPanel && selectedPolicy && (
        <>
          {/* Overlay */}
          <div 
            className="fixed inset-0 bg-black/50 z-40 transition-opacity"
            onClick={handleCloseCommissionsPanel}
          />
          
          {/* Slide-out Panel */}
          <div className="fixed inset-y-0 right-0 w-[600px] bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-[#d4af37] to-[#b8941e] text-white p-6 shadow-lg z-10">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-xl font-bold mb-2">Commissions</h3>
                  <div className="text-sm opacity-90 space-y-1">
                    <p><span className="font-semibold">Policy:</span> #{selectedPolicy.policy_number}</p>
                    <p><span className="font-semibold">Client:</span> {selectedPolicy.client}</p>
                    <p><span className="font-semibold">Agent:</span> {selectedPolicy.agentOwner_name}</p>
                  </div>
                </div>
                <button
                  onClick={handleCloseCommissionsPanel}
                  className="ml-4 p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Policy Information Section */}
              <Card className="mb-6 border-2 border-[#d4af37]">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-bold text-neutral-700 uppercase tracking-wide">Policy Information</h4>
                    <Button
                      onClick={() => {
                        setEditingPolicy(selectedPolicy);
                        setShowEditDialog(true);
                      }}
                      size="sm"
                      variant="outline"
                      className="h-8 border-[#d4af37] text-[#b8941e] hover:bg-[#d4af37] hover:text-white"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit Policy
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                    <div>
                      <p className="text-xs text-neutral-500">Policy Number</p>
                      <p className="font-semibold text-neutral-800">{selectedPolicy.policy_number || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-neutral-500">Client</p>
                      <p className="font-semibold text-neutral-800">{selectedPolicy.client || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-neutral-500">Agent on Record</p>
                      <p className="font-semibold text-neutral-800">{selectedPolicy.agentOwner_name || 'N/A'}</p>
                      <p className="text-xs text-neutral-500">{selectedPolicy.agentOwner_npn || ''}</p>
                    </div>
                    <div>
                      <p className="text-xs text-neutral-500">Carrier</p>
                      <p className="font-semibold text-neutral-800">{selectedPolicy.carrier_name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-neutral-500">Annual Premium</p>
                      <p className="font-semibold text-neutral-800">${selectedPolicy.annual_premium || '0'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-neutral-500">Status</p>
                      <p className="font-semibold text-neutral-800">{selectedPolicy.status_name || 'N/A'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Commissions Section Header */}
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-bold text-neutral-700 uppercase tracking-wide">
                  Commissions ({selectedPolicy.commission_of_policy?.length || 0})
                </h4>
                <Button
                  onClick={() => {
                    setSelectedPolicyId(selectedPolicy.id);
                    setShowAddCommissionModal(true);
                  }}
                  size="sm"
                  className="bg-gradient-to-r from-[#d4af37] to-[#b8941e] hover:from-[#b8941e] hover:to-[#d4af37] text-white h-8"
                >
                  <DollarSign className="w-4 h-4 mr-1" />
                  Add Commission
                </Button>
              </div>

              {/* Commissions List */}
              {selectedPolicy.commission_of_policy && selectedPolicy.commission_of_policy.length > 0 ? (
                <div className="space-y-3">
                  {selectedPolicy.commission_of_policy.map((commission, idx) => (
                    <Card key={commission.id || idx} className="border border-neutral-200 hover:border-[#d4af37] transition-all">
                      <CardContent className="p-4">
                        {/* Header: Amount and Badge */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <p className={`text-xl font-bold ${parseFloat(commission.commission_amount || 0) < 0 ? 'text-red-600' : 'text-green-600'}`}>
                              ${parseFloat(commission.commission_amount || 0).toFixed(2)}
                            </p>
                            <Badge className="bg-orange-500 text-white text-xs px-2 py-0.5">
                              {commission.paid_split || 'Unpaid'}
                            </Badge>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingCommission(commission);
                                setShowEditCommissionModal(true);
                              }}
                              className="h-7 w-7 p-0 hover:bg-[#d4af37]/10 hover:text-[#d4af37]"
                            >
                              <Edit className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeletingCommission(commission);
                                setShowDeleteDialog(true);
                              }}
                              className="h-7 w-7 p-0 hover:bg-red-100 hover:text-red-600"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>

                        {/* Commission Details - Compact */}
                        <div className="space-y-2 text-sm">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="text-xs text-neutral-500">Commission Agent</p>
                              <p className="font-medium text-neutral-800">{commission.agentonCommission_name || 'N/A'}</p>
                              <p className="text-xs text-neutral-500">NPN: {commission.agentonCommission_npn || 'N/A'}</p>
                            </div>
                            <div className="flex-1">
                              <p className="text-xs text-neutral-500">Submitted By</p>
                              <p className="font-medium text-neutral-800">{commission.submittedBy_name || 'N/A'}</p>
                            </div>
                          </div>

                          {/* Comment/Note */}
                          {commission.comment && (
                            <div className="pt-2 border-t border-neutral-100">
                              <p className="text-xs text-neutral-500 mb-1">Note:</p>
                              <p className="text-xs text-neutral-700">{commission.comment}</p>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <DollarSign className="w-16 h-16 mx-auto text-neutral-300 mb-4" />
                  <p className="text-neutral-500 mb-4">No commissions recorded yet</p>
                  <Button
                    onClick={() => {
                      setSelectedPolicyId(selectedPolicy.id);
                      setShowAddCommissionModal(true);
                    }}
                    className="bg-gradient-to-r from-[#d4af37] to-[#b8941e] hover:from-[#b8941e] hover:to-[#d4af37] text-white"
                  >
                    <DollarSign className="w-4 h-4 mr-2" />
                    Add First Commission
                  </Button>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function InfoField({ label, value }) {
  return (
    <div>
      <p className="text-xs text-neutral-500 mb-1">{label}</p>
      <p className="text-sm font-medium text-neutral-800">{value || 'N/A'}</p>
    </div>
  );
}
